import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Box, Button, Grid, Typography, TextField, IconButton, Chip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { useSelector } from "react-redux";
import Loading from "../../../../common/loader/loading";

const KitchenChecklist = ({ onClose, selectedTask, setOpenDefaultSnackbar }) => {
  const [time, setTime] = useState("00:00");
  const availableTimes = ["00:00", "06:00", "12:00", "18:00"];
  const [disabledTimes, setDisabledTimes] = useState([]);
  const [filteredData, setFilteredData] = useState(null);
  const [items, setItems] = useState([]); // Initialize as an empty array
  const user = useSelector((state) => state.auth.user);
  const initialItemsRef = useRef(JSON.stringify(items));
  const today = new Date();
  const todayDate = today.toISOString().split("T")[0];
  const [loading, setLoading] = useState(true); // Loading state
  const [taskId, setTaskId] = useState(selectedTask?.id);

  useEffect(() => {
    if (!taskId && selectedTask?.id) {
      setTaskId(selectedTask.id);
    }
  }, [selectedTask?.id]); // Run only if `selectedTask.id` is not set

  const updateSubTask = async (taskId) => {
    if (!taskId) {
      console.error("Task ID is undefined. Cannot update.");
      return;
    }

    try {
      await axios.put(
        `/api/assessments/updateTask/${taskId}`,
        {
          status: "completed",
          checklist: [],
          updated_by: user?.username || "Unknown",
        },
        {
          headers: {
            "Content-Type": "application/json",
            "x-user-role": user?.roleid || "",
          },
        }
      );
      setOpenDefaultSnackbar(true);
    } catch (error) {
      console.error("Error updating subtask:", error);
    }
  };

  // Fetch filtered data based on time and created_at
  useEffect(() => {
    const fetchFilteredData = async () => {
      setLoading(true); // Start loading
      setFilteredData(null); // Reset filtered data

      try {
        const response = await axios.get("/api/kitchens/kitchenItem/filter", {
          params: { time, created_at: todayDate },
        });

        const fetchedData = response.data.item; // Set the filtered data
        setFilteredData(fetchedData);

        // Initialize `items` based on the fetched data
        if (fetchedData?.items) {
          const mappedItems = fetchedData.items.map((item) => ({
            name: item.itemName,
            count: item.itemCount,
          }));
          setItems(mappedItems);
          initialItemsRef.current = JSON.stringify(mappedItems); // Save initial state for comparison
        }
      } catch (err) {
        console.error("Error fetching filtered data:", err);
      } finally {
        setLoading(false); // Stop loading
      }
    };

    fetchFilteredData();
  }, [time, todayDate]);
  const handleIncrement = (index) => {
    const updatedItems = [...items];
    updatedItems[index].count++;
    setItems(updatedItems);
  };

  const handleDecrement = (index) => {
    const updatedItems = [...items];
    if (updatedItems[index].count > 0) updatedItems[index].count--;
    setItems(updatedItems);
  };

  useEffect(() => {
    const currentHour = new Date().getHours();

    if (currentHour >= 18 && currentHour < 24) {
      setTime("18:00");
      setDisabledTimes(["00:00", "06:00", "12:00"]);
    } else if (currentHour >= 12 || currentHour < 18) {
      setTime("12:00");
      setDisabledTimes(["00:00", "06:00", "18:00"]);
    } else if (currentHour >= 6 || currentHour < 12) {
      setTime("06:00");
      setDisabledTimes(["00:00", "12:00", "18:00"]);
    } else {
      setTime("00:00");
      setDisabledTimes(["06:00", "12:00", "18:00"]);
    }
  }, []);

  const handleTimeChange = (selectedTime) => {
    setTime(selectedTime);
  };

  const handleCountChange = (e, index) => {
    const newValue = e.target.value;

    // Ensure the value is a valid number
    if (!isNaN(newValue) || newValue === "") {
      const updatedItems = [...items];
      updatedItems[index].count = newValue; // Update the specific item's count
      setItems(updatedItems); // Update state
    }
  };

  const handleSubmit = async () => {
    if (!filteredData || !filteredData.id) {
      console.error("Filtered data or ID is missing.");
      alert("Unable to submit. No valid checklist data found.");
      return;
    }

    const requestBody = {
      items: items.map((item) => ({
        itemName: item.name,
        itemCount: item.count,
      })),
      time: time,
      completed_by: user.username,
    };

    try {
      await axios.put(`/api/kitchens/kitchenItem/${filteredData?.id}`, requestBody);

      // Fetch the latest filtered data after submission
      const todayDate = new Date().toLocaleDateString("en-CA");
      const response = await axios.get("/api/kitchens/kitchenItemAll");

      if (response.data && Array.isArray(response.data.items)) {
        // Filter tasks where created_at matches today's date
        const filteredTasks = response.data.items.filter(
          (task) => task.created_at && new Date(task.created_at).toLocaleDateString("en-CA") === todayDate
        );

        // Check if all four tasks have a 'completed_by' value
        const allTasksCompleted =
          filteredTasks &&
          filteredTasks.length === 4 &&
          filteredTasks.every((task) => task.completed_by && task.completed_by !== "");

        if (allTasksCompleted) {
          updateSubTask(taskId);
        }

        onClose();
      }
    } catch (error) {
      console.error("Error submitting kitchen checklist:", error);
    }
  };

  const isDoneDisabled = () => {
    return initialItemsRef.current === JSON.stringify(items); // Compare with initial state
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <Box sx={{ p: 4, m: 2 }}>
      <Chip
        variant='contained'
        sx={{
          fontSize: "14px",
          backgroundColor: "#F2F6FF",
          color: "blue",
        }}
        label='Daily Tasks'
        size='small'
      />
      <Typography sx={{ fontSize: "24px", color: "black", fontWeight: "bold", mb: 2 }}>
        Daily Kitchen Item Check List
      </Typography>

      <Typography sx={{ fontSize: "14px", color: "#757575", mb: 3 }}>
        Date: {todayDate} <span style={{ color: "#1751D0" }}>• {user.username}</span>
      </Typography>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={10}>
          <Box sx={{ display: "flex", gap: 2 }}>
            {availableTimes
              .filter((timeValue) => {
                const currentHour = new Date().getHours();
                if (currentHour >= 8 && currentHour <= 20) {
                  return timeValue === "12:00" || timeValue === "18:00";
                }
                return true;
              })
              .map((timeValue) => (
                <Button
                  key={timeValue}
                  variant={time === timeValue ? "contained" : "outlined"}
                  onClick={() => handleTimeChange(timeValue)}
                  disabled={disabledTimes.includes(timeValue)} // Disable logic
                  sx={{
                    textTransform: "none",
                    borderRadius: "8px",
                  }}
                >
                  {timeValue}
                </Button>
              ))}
          </Box>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {items?.map((item, index) => (
          <Grid item md={6} sm={12} lg={6} key={index}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                border: "1px solid #ECECED",
                borderRadius: "8px",
                p: 1,
              }}
            >
              <Typography>{item.name}</Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <IconButton color='primary' size='small' onClick={() => handleDecrement(index)}>
                  <RemoveIcon />
                </IconButton>
                <TextField
                  value={item.count}
                  size='small'
                  onChange={(e) => handleCountChange(e, index)} // Add onChange event
                  inputProps={{
                    style: { textAlign: "center", width: "40px" },
                  }}
                />

                <IconButton color='primary' size='small' onClick={() => handleIncrement(index)}>
                  <AddIcon />
                </IconButton>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
      {filteredData === null ? (
        <Typography sx={{ fontSize: "18px", color: "black", fontWeight: "bold", mb: 2 }}>
          There are no any kitchen item check list tasks assigned for this time period for the day.
        </Typography>
      ) : (
        <Typography>
          All staff are to ensure there is the correct number of items always present within the home. Any items that
          are missing are to be looked for. Any concerns please inform the duty manager. Any damaged items or items that
          have been replaced; the home manager is to be informed.
        </Typography>
      )}
      <Grid item xs={12} mt={2}>
        <Grid container spacing={1} className='submit-button-container'>
          <Grid item>
            <Button
              variant='outlined'
              color='primary'
              className='submit-button'
              sx={{ textTransform: "none" }}
              onClick={() => onClose()}
            >
              Previous
            </Button>
          </Grid>
          <Grid item>
            <Button
              type='submit'
              variant='contained'
              color='primary'
              className='submit-button'
              sx={{ textTransform: "none" }}
              onClick={handleSubmit}
              disabled={isDoneDisabled()} // Disable if no changes
            >
              Done
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

export default KitchenChecklist;
